import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { prepararFotoProducto } from '../../../core/media/prepararFotoProducto.js';
import { createWithFirestoreRetry } from '../../../core/firestore/withFirestoreRetry.js';

export const COL_PROFESIONALES_SERVICIOS = 'profesionales_servicios';
export const SUB_TARJETAS = 'tarjetas';

/**
 * @param {{ getFirebase: () => object }} opts
 */
export function createServiciosRepository({ getFirebase }) {
  const withRetry = createWithFirestoreRetry({ getFirebase });

  function profRef(uid) {
    const { db } = getFirebase();
    return doc(db, COL_PROFESIONALES_SERVICIOS, String(uid));
  }

  function tarjetasCol(uid) {
    const { db } = getFirebase();
    return collection(db, COL_PROFESIONALES_SERVICIOS, String(uid), SUB_TARJETAS);
  }

  /**
   * @returns {Promise<import('firebase/firestore').DocumentData | null>}
   */
  async function obtenerProfesional(uid) {
    const { db } = getFirebase();
    const ref = doc(db, COL_PROFESIONALES_SERVICIOS, String(uid));
    const snap = await withRetry(() => getDoc(ref));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  /** Listado MVP: todos los perfiles publicados (límite). Orden: actualizadoEn desc requiere índice; por simplicidad sin orderBy en servidor. */
  async function listarProfesionales({ max = 80 } = {}) {
    const { db } = getFirebase();
    const q = query(collection(db, COL_PROFESIONALES_SERVICIOS), limit(Math.min(120, max)));
    const snap = await withRetry(() => getDocs(q));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  /**
   * @param {string} uid
   * @param {{
   *   rubros: string[],
   *   disponibilidad: string,
   *   disponibilidad_semana?: { dia: number, desde: string, hasta: string }[],
   *   descripcion_publica: string,
   *   nombre_publico: string,
   *   telefono_whatsapp: string,
   *   frase_personal?: string,
   *   foto_perfil_servicios_url?: string,
   *   perfil_publico?: boolean,
   * }} payload
   */
  async function guardarMiPerfil(uid, payload) {
    const ref = profRef(uid);
    const snap = await withRetry(() => getDoc(ref));
    const slots = Array.isArray(payload.disponibilidad_semana) ? payload.disponibilidad_semana : [];
    const base = {
      rubros: payload.rubros,
      disponibilidad: payload.disponibilidad,
      disponibilidad_semana: slots,
      descripcion_publica: payload.descripcion_publica || '',
      nombre_publico: String(payload.nombre_publico || '').trim().slice(0, 120),
      telefono_whatsapp: String(payload.telefono_whatsapp || '').trim().slice(0, 40),
      frase_personal: String(payload.frase_personal || '').trim().slice(0, 180),
      foto_perfil_servicios_url: String(payload.foto_perfil_servicios_url || '').trim().slice(0, 2048),
      perfil_publico: payload.perfil_publico === true,
      actualizadoEn: serverTimestamp(),
    };
    if (!snap.exists()) {
      await withRetry(() =>
        setDoc(ref, {
          ...base,
          tarjetas_saldo: 0,
          servicios_premium: false,
          perfil_validado: false,
          ubicacion_barrio: '',
          creadoEn: serverTimestamp(),
        })
      );
    } else {
      await withRetry(() => updateDoc(ref, base));
    }
  }

  /** Sube foto de perfil del profesional (webp bajo `usuarios/{uid}/servicios_perfil/`). */
  async function subirFotoPerfilServicios(uid, file) {
    const prep = await prepararFotoProducto(file);
    const path = `usuarios/${String(uid)}/servicios_perfil/foto.webp`;
    const { storage } = getFirebase();
    const r = ref(storage, path);
    await uploadBytes(r, prep.blob, { contentType: 'image/webp' });
    return getDownloadURL(r);
  }

  /**
   * @param {string} uidProfesional
   * @param {{ pageSize?: number, startAfterDoc?: import('firebase/firestore').QueryDocumentSnapshot|null }} opts
   */
  async function listarTarjetasVisibles(uidProfesional, { pageSize = 12, startAfterDoc = null } = {}) {
    const { db } = getFirebase();
    const base = collection(db, COL_PROFESIONALES_SERVICIOS, String(uidProfesional), SUB_TARJETAS);
    const lim = Math.min(50, pageSize);
    const q = startAfterDoc
      ? query(base, orderBy('fecha_publicacion', 'desc'), startAfter(startAfterDoc), limit(lim))
      : query(base, orderBy('fecha_publicacion', 'desc'), limit(lim));
    const snap = await withRetry(() => getDocs(q));
    return {
      docs: snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((t) => t.oculta_por_admin !== true && t.visible !== false),
      lastDoc: snap.docs.length ? snap.docs[snap.docs.length - 1] : null,
    };
  }

  /**
   * Publicar tarjeta: transacción descuenta saldo y crea doc (fotos ya subidas a Storage).
   * @param {string} uid
   * @param {{ titulo_caso: string, cliente_display: string, duracion_txt: string, fotos_antes: string[], fotos_despues: string[] }} datos
   */
  /**
   * Sube 4 fotos (mismos límites que productos). Orden: antes×2, después×2.
   */
  async function subirFotosTarjeta(uid, tarjetaId, files) {
    const { storage } = getFirebase();
    const list = Array.from(files || []);
    if (list.length !== 4) throw new Error('Tenés que elegir 4 fotos (2 antes, 2 después).');
    const names = ['antes_0', 'antes_1', 'despues_0', 'despues_1'];
    const urls = [];
    for (let i = 0; i < 4; i++) {
      const prep = await prepararFotoProducto(list[i]);
      const path = `usuarios/${String(uid)}/servicios_tarjetas/${String(tarjetaId)}/${names[i]}.webp`;
      const r = ref(storage, path);
      await uploadBytes(r, prep.blob, { contentType: 'image/webp' });
      urls.push(await getDownloadURL(r));
    }
    return { fotos_antes: urls.slice(0, 2), fotos_despues: urls.slice(2, 4) };
  }

  /**
   * Valida código vecino o UID (misma callable que ventas). Devuelve UID y texto para la tarjeta.
   */
  async function resolverTagCompradorTarjeta(codigoRaw) {
    const { fns } = getFirebase();
    if (!fns) throw new Error('Servicio no disponible. Probá más tarde.');
    const call = httpsCallable(fns, 'resolverCompradorPorCodigo');
    const res = await call({ codigo: String(codigoRaw || '').trim() });
    const data = res?.data || {};
    const compradorUid = String(data.uid || '').trim();
    if (!compradorUid) throw new Error('No encontramos ese tag.');
    const etiqueta_cliente = String(data.etiqueta_cliente || 'Vecino').trim().slice(0, 80) || 'Vecino';
    return { uid: compradorUid, etiqueta_cliente };
  }

  async function publicarTarjeta(uid, datos, tarjetaIdFijo = null) {
    const { db } = getFirebase();
    const tid = tarjetaIdFijo || doc(tarjetasCol(uid)).id;
    const tRef = doc(db, COL_PROFESIONALES_SERVICIOS, String(uid), SUB_TARJETAS, tid);
    const pRef = profRef(uid);

    const titulo_caso = String(datos.titulo_caso || '').trim();
    const cliente_display = String(datos.cliente_display || '').trim();
    const comprador_uid = String(datos.comprador_uid || '').trim();
    const duracion_txt = String(datos.duracion_txt || '').trim();
    const fotos_antes = Array.isArray(datos.fotos_antes) ? datos.fotos_antes.map(String) : [];
    const fotos_despues = Array.isArray(datos.fotos_despues) ? datos.fotos_despues.map(String) : [];

    if (titulo_caso.length < 2) throw new Error('Completá el título del trabajo.');
    if (cliente_display.length < 1) throw new Error('Verificá el tag del cliente.');
    if (comprador_uid.length < 10 || comprador_uid.length > 128) throw new Error('Tag del cliente inválido.');
    if (comprador_uid === String(uid)) throw new Error('No podés usar tu propio usuario como cliente.');
    if (duracion_txt.length < 1) throw new Error('Indicá duración o tipo de trabajo.');
    if (fotos_antes.length !== 2 || fotos_despues.length !== 2) {
      throw new Error('Subí 2 fotos del antes y 2 del después.');
    }

    await runTransaction(db, async (tx) => {
      const ps = await tx.get(pRef);
      if (!ps.exists()) throw new Error('Primero completá tu perfil de servicios.');
      const saldo = Number(ps.data()?.tarjetas_saldo) || 0;
      if (saldo < 1) throw new Error('No tenés tarjetas disponibles. Pedí saldo a operaciones o comprá más.');
      tx.update(pRef, {
        tarjetas_saldo: saldo - 1,
        actualizadoEn: serverTimestamp(),
      });
      tx.set(tRef, {
        profesional_uid: String(uid),
        titulo_caso,
        cliente_display: cliente_display.slice(0, 80),
        duracion_txt: duracion_txt.slice(0, 120),
        fotos_antes: fotos_antes.slice(0, 2),
        fotos_despues: fotos_despues.slice(0, 2),
        fecha_publicacion: serverTimestamp(),
        visible: true,
        oculta_por_admin: false,
        reseña_estrellas: null,
        reseña_texto: null,
        comprador_uid,
      });
    });
    return tid;
  }

  /** Flujo completo: genera id, sube fotos, transacción publicación. */
  async function publicarTarjetaDesdeFormulario(uid, texto, files) {
    const tid = doc(tarjetasCol(uid)).id;
    const { fotos_antes, fotos_despues } = await subirFotosTarjeta(uid, tid, files);
    return publicarTarjeta(
      uid,
      {
        titulo_caso: texto.titulo_caso,
        cliente_display: texto.cliente_display,
        comprador_uid: texto.comprador_uid,
        duracion_txt: texto.duracion_txt,
        fotos_antes,
        fotos_despues,
      },
      tid
    );
  }

  return {
    obtenerProfesional,
    listarProfesionales,
    guardarMiPerfil,
    subirFotoPerfilServicios,
    listarTarjetasVisibles,
    resolverTagCompradorTarjeta,
    publicarTarjeta,
    publicarTarjetaDesdeFormulario,
    subirFotosTarjeta,
    profRef,
    tarjetasCol,
  };
}
